import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    service: '',
    message: '',
    urgency: 'standard'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: '📞',
      title: 'Call Us',
      details: '+61 1300 989 330',
      description: 'Sun-Fri: 24 Hours',
      link: 'tel:+611300989330',
      color: 'blue'
    },
    {
      icon: '✉️',
      title: 'Email Us',
      details: 'greenrockycarpet@gmail.com',
      description: 'We respond within 2 hours',
      link: 'mailto:greenrockycarpet@gmail.com',
      color: 'green'
    },
    {
      icon: '📍',
      title: 'Visit Us',
      details: '2/10 Thompson St, Park Avenue',
      description: 'QLD 4701, Australia',
      link: 'https://maps.google.com',
      color: 'purple'
    },
    {
      icon: '🕒',
      title: 'Business Hours',
      details: 'Sunday - Friday',
      description: '24 Hours',
      link: null,
      color: 'orange'
    }
  ];

  const urgencyOptions = [
    { value: 'standard', label: 'Standard Inquiry (2-3 business days)' },
    { value: 'priority', label: 'Priority (24-48 hours)' },
    { value: 'emergency', label: 'Emergency (Same day response)' }
  ];

  const serviceOptions = [
    'General Inquiry',
    'Standard Carpet Cleaning',
    'Premium Deep Cleaning',
    'Stain Removal Service',
    'Move-in/Move-out Cleaning',
    'Commercial Cleaning',
    'Emergency Services',
    'Other'
  ];

  const subjectOptions = [
    'Request a Quote',
    'Service Question',
    'Complaint',
    'Partnership',
    'Employment',
    'Other'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after success
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        service: '',
        message: '',
        urgency: 'standard'
      });
      setIsSubmitted(false);
    }, 5000);
  };

  const stats = [
    { number: '15 min', label: 'Avg Response Time' },
    { number: '24/7', label: 'Emergency Support' },
    { number: '99%', label: 'Satisfaction Rate' },
    { number: '6K+', label: 'Customers Helped' }
  ];

  return (
  <>
      <SEO 
        title="Contact GreenRocky Carpet - Get Free Quote"
        description="Contact GreenRocky Carpet for professional carpet cleaning services. Get free estimates, schedule appointments, or ask questions about our eco-friendly cleaning solutions."
        keywords="contact carpet cleaners, free estimate, schedule appointment, carpet cleaning quote, customer service"
        url="https://greenrockycarpet.com.au/contact"
        section="Contact"
      />


    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Get In Touch</h1>
            <p className="hero-subtitle">
              We're here to help with all your carpet cleaning needs. Reach out to us and experience the GreenRocky difference.
            </p>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div key={index} className="hero-stat">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="contact-container">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info-section">
              <div className="section-header">
                <div className="section-tag">Contact Info</div>
                <h2 className="section-title">Let's Start a Conversation</h2>
                <p className="section-subtitle">
                  Choose the most convenient way to reach us. Our team is ready to assist you.
                </p>
              </div>

              <div className="contact-cards">
                {contactInfo.map((contact, index) => (
                  <a
                    key={index}
                    href={contact.link}
                    className={`contact-card ${contact.color} ${!contact.link ? 'no-link' : ''}`}
                    target={contact.link ? "_blank" : undefined}
                    rel={contact.link ? "noopener noreferrer" : undefined}
                  >
                    <div className="contact-icon">{contact.icon}</div>
                    <div className="contact-content">
                      <h3 className="contact-title">{contact.title}</h3>
                      <p className="contact-details">{contact.details}</p>
                      <p className="contact-description">{contact.description}</p>
                    </div>
                    {contact.link && (
                      <div className="contact-arrow">→</div>
                    )}
                  </a>
                ))}
              </div>

              {/* Emergency CTA */}
              <div className="emergency-cta">
                <div className="emergency-icon">🚨</div>
                <div className="emergency-content">
                  <h3>Emergency Service Available</h3>
                  <p>24/7 support for urgent cleaning needs</p>
                  <a href="tel:+611300989330" className="emergency-btn">
                    Call Now: +61 1300 989 330
                  </a>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="trust-section">
                <h3>Why Choose Us</h3>
                <div className="trust-badges">
                  <div className="trust-badge">
                    <span className="badge-icon">🏆</span>
                    <span>15+ Years Experience</span>
                  </div>
                  <div className="trust-badge">
                    <span className="badge-icon">🌿</span>
                    <span>Eco-Friendly Solutions</span>
                  </div>
                  <div className="trust-badge">
                    <span className="badge-icon">⭐</span>
                    <span>5-Star Rated Service</span>
                  </div>
                  <div className="trust-badge">
                    <span className="badge-icon">🛡️</span>
                    <span>Fully Insured</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-section">
              <div className="form-container">
                <div className="form-header">
                  <h2>Send Us a Message</h2>
                  <p>Fill out the form below and we'll get back to you promptly</p>
                </div>

                {isSubmitted ? (
                  <div className="success-message">
                    <div className="success-icon">✅</div>
                    <div className="success-content">
                      <h3>Message Sent Successfully!</h3>
                      <p>Thank you for contacting us. We'll get back to you within 1 hours.</p>
                      <button 
                        onClick={() => setIsSubmitted(false)}
                        className="btn-secondary"
                      >
                        Send Another Message
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+61 0000 000 000"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="urgency">Response Urgency</label>
                        <select
                          id="urgency"
                          name="urgency"
                          value={formData.urgency}
                          onChange={handleChange}
                        >
                          {urgencyOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="subject">Subject *</label>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select a subject</option>
                          {subjectOptions.map((option, index) => (
                            <option key={index} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="service">Service Interest</label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                        >
                          <option value="">Select a service</option>
                          {serviceOptions.map((option, index) => (
                            <option key={index} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        rows="6"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your carpet cleaning needs, specific concerns, or any questions you might have..."
                      ></textarea>
                      <div className="char-count">
                        {formData.message.length}/500 characters
                      </div>
                    </div>

                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="spinner"></div>
                          Sending Message...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </button>

                    <div className="form-note">
                      <p>📞 Prefer to talk? Call us at <a href="tel:+611300989330">+61 1300 989 330</a></p>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="contact-faq">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Quick answers to common questions about our services
            </p>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>How quickly do you respond to inquiries?</h4>
              <p>We respond to all inquiries within 2 hours during business hours. Emergency requests receive immediate attention.</p>
            </div>
            <div className="faq-item">
              <h4>Do you offer free estimates?</h4>
              <p>Yes! We provide free, no-obligation estimates for all our services. You can get an instant estimate online or schedule an on-site assessment.</p>
            </div>
            <div className="faq-item">
              <h4>What areas do you serve?</h4>
              <p>We serve the entire Green City metropolitan area and surrounding communities. Contact us to confirm service availability in your area.</p>
            </div>

          </div>
        </div>
      </section>
    </div>
  </>
  );
};

export default Contact;